export const MEMO_AREAS = ['자율', '동아리', '진로', '교과', '행동'];
export const MEMO_MAX_CHARS = 500;

const AREA_HINTS = [
  ['동아리', /동아리/],
  ['진로', /진로|직업|장래|꿈|희망/],
  ['교과', /수업|과제|실험|발표|문제|풀이|국어|수학|영어|과학|사회|역사|음악|미술|체육|기술|가정|정보|도덕/],
  ['자율', /회장|부회장|반장|학급|자치|행사|축제|봉사|캠페인|회의/],
];

/** 메모 내용으로 영역을 제안한다. 모르면 빈 값(선생님이 고르지 않아도 된다). */
export const suggestMemoArea = (text) => {
  const body = String(text || '');
  const hit = AREA_HINTS.find(([, pattern]) => pattern.test(body));
  return hit ? hit[0] : '';
};

const RESTRICTED = [
  // '대상으로'(설문 대상 등)는 수상이 아니다.
  ['수상', /수상|상을 받|상장|입상|(?:금|은|동|장려|우수|최우수|특|으뜸|버금)상(?:을|이|\s|$|[.,])|대상을 (?:받|차지)/],
  ['어학시험', /토익|토플|텝스|TOEIC|TOEFL|TEPS|HSK|JLPT|DELF|DELE|공인어학|어학 ?시험/i],
  ['방과후', /방과후/],
  ['교외 대회·논문·특허', /교외|외부 대회|논문|특허|출간|출판/],
];

/** 생기부 문장에 쓸 수 없는 내용(수상·어학시험·방과후 등)이 메모에 있으면 그 이름들. */
export const restrictedMemoKinds = (text) => {
  const body = String(text || '');
  return RESTRICTED.filter(([, pattern]) => pattern.test(body)).map(([label]) => label);
};

// 메모에 '메모로만 남겨 두세요'라고 알린 문장은 AI 입력에서 뺀다(같은 메모의 다른 문장은 넣는다).
const writableMemoText = (text) => String(text || '')
  .replace(/([.!?])\s+/g, '$1\n')
  .split(/\n+/)
  .map((sentence) => sentence.trim())
  .filter((sentence) => sentence && restrictedMemoKinds(sentence).length === 0)
  .join(' ');

/** 학생 화면 '생기부 쓰기'에서 고르는 영역. area: 그 영역 메모(빈 값이면 모든 메모). */
export const SINGLE_CATEGORIES = [
  { category: '행동발달', label: '행동특성', area: '' },
  { category: '교과', label: '교과세특', area: '교과' },
  { category: '창체', label: '자율', area: '자율' },
  { category: '동아리', label: '동아리', area: '동아리' },
  { category: '진로', label: '진로', area: '진로' },
];

const categoryArea = category => SINGLE_CATEGORIES.find(item => item.category === category)?.area || '';

/** 다중 생성 종류를 메모 고르는 기준으로 바꾼다. 교과세특(고등)은 교과세특과 같은 메모를 쓴다. */
export const memoCategoryOf = category => (category === '교과_고등' ? '교과' : category);

/** 영역에 맞는 메모: 행동특성은 모든 메모, 다른 영역은 그 영역 메모와 영역을 고르지 않은 메모. */
export const memosForCategory = (memos = [], category = '행동발달') => {
  const area = categoryArea(category);
  return area ? memos.filter(memo => !memo.area || memo.area === area) : [...memos];
};

const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// 메모에 적힌 학생 이름(명단 이름·별칭)은 '학생'으로 바꾼다. 생기부 입력에 이름을 넣지 않는다.
const maskNames = (text, names = []) => [...new Set(names.map(name => String(name || '').trim()).filter(name => name.length >= 2))]
  .sort((a, b) => b.length - a.length)
  .reduce((acc, name) => acc.replace(new RegExp(escapeRegExp(name), 'g'), '학생'), text);

const shortDay = (value) => {
  const [, month, day] = String(value || '').split('-').map(Number);
  return month && day ? `${month}/${day}` : '';
};

const byDate = (a, b) => String(a.date || '').localeCompare(String(b.date || ''));

/**
 * 생기부로 넘길 근거 목록(날짜·영역·사실). 이름은 지운다. 오래된 것부터.
 * kind: 'memo'(선생님 메모) | 'scoring'(채점 결과에서 모은 근거)
 * source: 'typed' | 'group'(여러 명이 함께) | 'paste' | 'scoring'
 */
export const buildEvidenceItems = ({ memos = [], scoring = [] } = {}, { category = '행동발달', names = [] } = {}) => {
  const memoItems = memosForCategory(memos, category)
    .map(memo => ({
      kind: 'memo',
      date: memo.date || '',
      area: memo.area || '',
      text: maskNames(writableMemoText(memo.text), names),
      source: memo.source || 'typed',
    }));
  const scoringItems = scoring.map(item => ({
    kind: 'scoring',
    date: String(item.date || '').slice(0, 10),
    area: '교과',
    text: maskNames([item.title ? `「${item.title}」` : '', item.line || ''].filter(Boolean).join(' '), names),
    source: 'scoring',
  }));
  return [...memoItems, ...scoringItems].filter(item => item.text).sort(byDate);
};

const evidenceLine = (item) => {
  const head = [shortDay(item.date), item.kind === 'scoring' ? '채점' : item.area].filter(Boolean).join(' · ');
  const body = item.source === 'group' ? `(여러 명이 함께) ${item.text}` : item.text;
  return `- ${head ? `${head} · ` : ''}${body}`;
};

const ROLE_CATEGORIES = ['행동발달', '창체'];
const CAREER_CATEGORIES = ['행동발달', '진로'];

/** 영역에 맞는 고정 정보만 남긴다: 임원은 행동특성·자율, 진로희망은 행동특성·진로. */
export const profileForCategory = (profile = {}, category = '행동발달') => {
  const area = ROLE_CATEGORIES.includes(category);
  return {
    role: area ? profile?.role || '' : '',
    role_period: area ? profile?.role_period || '' : '',
    career: CAREER_CATEGORIES.includes(category) ? profile?.career || '' : '',
  };
};

/** 고정 정보 줄과 근거 줄(한 줄에 근거 하나, 오래된 것부터). 이름은 지운다. */
export const buildMemoEvidenceLines = ({ profile = {}, memos = [], scoring = [] } = {}, options = {}) => {
  const fixed = [];
  if (profile.role) fixed.push(`임원: ${profile.role}${profile.role_period ? ` (${profile.role_period})` : ''}`);
  if (profile.career) fixed.push(`진로희망: ${profile.career}`);
  return { fixed, evidence: buildEvidenceItems({ memos, scoring }, options).map(evidenceLine) };
};

/**
 * 단일 생성 입력칸에 넣을 글: 고정 정보 + 날짜·영역이 붙은 사실 목록(오래된 것부터). 이름은 넣지 않는다.
 * 교과·동아리·진로 화면은 넘겨받은 글을 [구역] 머리말로 나눠 칸에 채우므로 그 머리말에 맞춘다.
 */
export const buildMemoPrompt = ({ profile = {}, memos = [], scoring = [] } = {}, options = {}) => {
  const { fixed, evidence } = buildMemoEvidenceLines({ profile, memos, scoring }, options);
  if (fixed.length === 0 && evidence.length === 0) return '';
  const category = options.category || '행동발달';
  if (category === '교과') return `[활동명/성취기준]\n\n\n[활동내용]\n${[...fixed, ...evidence].join('\n')}`;
  if (category === '동아리') return `[동아리 정보]\n\n\n[학생 활동 내용]\n${[...fixed, ...evidence].join('\n')}`;
  if (category === '진로') {
    const info = fixed.filter(line => line.startsWith('진로희망'));
    const rest = fixed.filter(line => !line.startsWith('진로희망'));
    return `[진로 활동]\n${[...rest, ...evidence].join('\n')}\n\n[학생 정보]\n${info.join('\n')}`;
  }
  return [...fixed, ...evidence].join('\n');
};

/** 학기 말(7월, 12월~2월)에만 '메모 없는 학생'을 알려 준다. */
export const isSemesterEnd = (date = new Date()) => [7, 12, 1, 2].includes(date.getMonth() + 1);
